import _ from "lodash";
/** external */
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  MinusCircleOutlined,
  InformationOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import classNames from "classnames";
/** internal */
import { useI18n } from "Hooks";
import {
  deleteQuestionField,
  deleteQuestionFieldV2,
} from "Worksheet/modules/WorksheetModules";
import {
  getResolvedFieldLabel,
  isMutationAndQueriesV2Enabled,
} from "Worksheet/modules/WorksheetUtils";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
import {
  QUESTION_FIELD_ICON_MAPPING,
  DYNAMIC_ANSWER_FIELDS,
  FIELD_UID_ADD_LEARNING_GOALS,
  MODE_TEACHER_CREATE,
  WORKSHEET_MODES,
} from "Worksheet/modules/WorksheetConstants";
import { removeAssessmentTools } from "Assessments/modules/AssessmentModule";
import {
  deleteLibraryQuestionField,
  deleteLibraryQuestionFieldV2,
} from "QuestionBank/modules/QuestionBankModule";
/** relative */
import classes from "./QuestionFieldLabel.scss";

const QuestionFieldLabel = props => {
  const {
    id,
    deleteQuestionField,
    isRemoveBtnEnabled,
    icon,
    label,
    labelLocale,
    subTextLocale,
    isRequired,
    isQuestionField,
    data,
    questionId,
    assessmentTools,
    removeAssessmentTools,
    informationText,
    deleteLibraryQuestionField,
    deleteLibraryQuestionFieldV2,
    isFromInternalTool,
    portalType,
    extraRightButtonComponent,
    learningGoalsFields,
    mode,
    isSubLabel,
    isSubQuestion,
    isQueriesV2Enabled,
    deleteQuestionFieldV2,
  } = props;

  const { t } = useI18n(["common", "unitPlan", "worksheet"]);

  const deleteQuestionFieldHandler = isFromInternalTool
    ? isMutationAndQueriesV2Enabled()
      ? deleteLibraryQuestionFieldV2
      : deleteLibraryQuestionField
    : isQueriesV2Enabled
    ? deleteQuestionFieldV2
    : deleteQuestionField; //WORKSHEET_RESTRUCTURING:Remove this once all questions are migrated to queries v2

  const isCreateMode = _.isEqual(mode, MODE_TEACHER_CREATE);

  const removeAssessmentToolsWithField = () => {
    const toolsToRemove = _.map(
      _.get(assessmentTools, "edges", []),
      assessmentTool => ({
        parentType: "QUESTION",
        parentId: questionId,
        id: assessmentTool.node.assessmentToolItem.id,
        type: assessmentTool.node.assessmentToolType,
        portalType,
        writeToCache: true,
      })
    );

    if (_.size(toolsToRemove) > 0) removeAssessmentTools(toolsToRemove);

    setTimeout(() => {
      deleteQuestionFieldHandler({
        ids: [id],
        isAnswerField: false,
        questionId,
      });
    }, 0);
  };

  /**
   * @description Remove all the learning goals related fields when removing `Add learning goals` field
   * @param {string} addLearningGoalsId - `Add learning goals` field id
   */
  const removeLearningGoalsWithFields = addLearningGoalsId => {
    const learningGoalsFieldsId = [addLearningGoalsId];

    /** adding only created fields ids to remove */
    _.forEach(data, field => {
      if (_.includes(_.keys(learningGoalsFields), _.get(field, "uid"))) {
        learningGoalsFieldsId.push(_.get(field, "id"));
      }
    });

    deleteQuestionFieldHandler({
      ids: learningGoalsFieldsId,
      isAnswerField: false,
      questionId,
    });
  };

  const removeField = () => {
    const fieldUid = _.get(_.find(data, ["id", id]), "uid");

    if (fieldUid === "measureAssessing") {
      removeAssessmentToolsWithField();
    } else if (fieldUid === FIELD_UID_ADD_LEARNING_GOALS) {
      removeLearningGoalsWithFields(id);
    } else {
      deleteQuestionFieldHandler({
        ids: [id],
        isAnswerField: _.includes(DYNAMIC_ANSWER_FIELDS, fieldUid),
        questionId,
        isSubQuestion,
      });
    }
  };

  const subTextClasses = "text-body-2 text-textSubtle";
  const labelContainerClasses = classNames({
    [classes.labelContainer]: true,
    [classes.questionFieldLabel]: isQuestionField,
  });

  const labelClasses = isSubLabel ? "text-label-3" : "text-label-2";

  return (
    <div
      className={labelContainerClasses}
      role="group"
      aria-labelledby={`label-${id}`}
    >
      <div className={classes.labelLeftContainer}>
        {React.isValidElement(icon)
          ? icon
          : _.isString(icon)
          ? _.get(QUESTION_FIELD_ICON_MAPPING, icon, null)
          : null}
        <div className={classes.content}>
          <div className={classes.label} id={`label-${id}`}>
            {labelLocale || label ? (
              <div className={labelClasses}>
                {labelLocale
                  ? getResolvedFieldLabel(t(labelLocale), isRequired)
                  : label}
              </div>
            ) : null}
            {informationText ? (
              <Tooltip
                placement="top"
                tooltip={
                  <div className={classes.tooltipText}>
                    {t(informationText)}
                  </div>
                }
              >
                <InformationOutlined variant="subtle" size="xxx-small" />
              </Tooltip>
            ) : null}
          </div>
          {subTextLocale ? (
            _.isArray(subTextLocale) ? (
              <div className={classes.subText}>
                {subTextLocale.map(subText => {
                  return (
                    <div className={subTextClasses} key={subText}>
                      {t(subText)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={subTextClasses}>{t(subTextLocale)}</div>
            )
          ) : null}
        </div>
      </div>
      <div className={classes.buttonContainer}>
        {React.isValidElement(extraRightButtonComponent)
          ? extraRightButtonComponent
          : null}
        {isRemoveBtnEnabled ? (
          <Tooltip
            placement="bottom"
            tooltip={t("common:delete_with_label", {
              label: t("unitPlan:field"),
            })}
          >
            <IconButton
              onClick={removeField}
              icon={isCreateMode ? <DeleteOutlined /> : <MinusCircleOutlined />}
              dsVersion="2.0"
              size={isCreateMode ? "x-small" : "small"}
              _legacy_variant="plain"
              variant="neutral"
              type="plain"
              testId="worksheet-questionFieldLabel-removeField"
              aria-label={t("common:delete_with_label", {
                label: t("unitPlan:field"),
              })}
            />
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { isFromInternalTool } = ownProps;

  //Fetching feature flag value here to avoid passing isQueriesV2Enabled prop through all parent components
  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library and not from Internal Tool)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled(
    false,
    isFromInternalTool
  );
  return {
    isQueriesV2Enabled,
  };
};

const mapActionCreators = {
  deleteQuestionField,
  removeAssessmentTools,
  deleteLibraryQuestionField,
  deleteLibraryQuestionFieldV2,
  deleteQuestionFieldV2,
};

QuestionFieldLabel.propTypes = {
  id: PropTypes.string,
  isRemoveBtnEnabled: PropTypes.bool,
  icon: PropTypes.any,
  label: PropTypes.string,
  labelLocale: PropTypes.string,
  subTextLocale: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isRequired: PropTypes.bool,
  isQuestionField: PropTypes.bool,
  assessmentTools: PropTypes.object,
  questionId: PropTypes.string,
  data: PropTypes.array,
  isFromInternalTool: PropTypes.bool,
  learningGoalsFields: PropTypes.objectOf(PropTypes.object),
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  isSubLabel: PropTypes.bool,
  isSubQuestion: PropTypes.bool,
};

QuestionFieldLabel.defaultProps = {
  isRemoveBtnEnabled: false,
  isRequired: false,
  isFromInternalTool: false,
  learningGoalsFields: {},
  isSubLabel: false,
};

export default connect(mapStateToProps, mapActionCreators)(QuestionFieldLabel);
